import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Truck } from "lucide-react";
import ClientBrand from "@/components/app/ClientBrand";
import PoweredBySmScope from "@/components/app/PoweredBySmScope";
import { brand } from "@/lib/branding";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@soilhaul.com");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left form pane */}
      <div className="flex flex-col justify-between p-10 lg:p-16 border-r border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-gray-900 flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-xl leading-none tracking-tight">SOILHAUL</div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-gray-500 mt-0.5">{brand.tagline}</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 py-16">
          <ClientBrand variant="stacked" className="mb-8" />
          <div className="label-caps mb-3">Operator Portal</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none text-gray-900 mb-3">
            Sign in to the yard.
          </h1>
          <p className="text-gray-600 mb-10 text-sm leading-relaxed">
            Track soil hauling, fleet status, and generate federally compliant bills of lading.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-caps block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-mono text-sm"
                placeholder="operator@company.com"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 font-mono text-sm"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="text-sm px-3 py-2 bg-red-50 border border-red-200 text-red-800" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full bg-orange-500 text-white font-semibold uppercase tracking-widest text-xs py-3 hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-mono">
            © SoilHaul 2026
          </div>
          <PoweredBySmScope className="border-t-0 py-0 bg-transparent justify-start" />
        </div>
      </div>

      {/* Right image pane */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1597691306883-ac4e882e7cc4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzb2lsJTIwZGlydCUyMHRleHR1cmV8ZW58MHx8fHwxNzgyMzg0MjAzfDA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-3 text-orange-400">
            FIELD OPS · REAL-TIME
          </div>
          <div className="font-display font-black text-4xl leading-none tracking-tight max-w-md">
            Every yard. Every haul. Every regulation.
          </div>
        </div>
      </div>
    </div>
  );
}
